import { ENV } from '@app/constants/env';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { getToken } from '../../../src/helpers/authProvider';

const rawBaseQuery = fetchBaseQuery({
  baseUrl: `${ENV.BASE_API_URL +ENV.MASTERS_APIURL}`,
});

const authBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  incomingArgs,
  api,
  extraOptions
) => {
  let args: string | FetchArgs = incomingArgs;

  // normalize string arg to object so we can set headers
  if (typeof args === 'string') {
    args = { url: args };
  }

  try {
    const token = await getToken();
    if (token) {
      // Check if body is FormData - if so, don't create new Headers object
      // because it will lose the multipart boundary
      if ((args as FetchArgs).body instanceof FormData) {
        // For FormData, just add Authorization to existing headers or create minimal headers
        const headers = new Headers();
        headers.set('Authorization', `Bearer ${token}`);
        (args as FetchArgs).headers = headers;
        // Don't set Content-Type - browser will set it with boundary
      } else {
        const headers =
          (args as FetchArgs).headers instanceof Headers
            ? (args as FetchArgs).headers
            : new Headers((args as FetchArgs).headers);

        headers.set('Authorization', `Bearer ${token}`);
        (args as FetchArgs).headers = headers;
      }
    }

    const result = await rawBaseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 401 && token) {
      // Handle token refresh if needed
    }

    return result;
  } catch {
    return { error: { status: 500, data: 'Token retrieval failed' } } as any;
  }
};

 export interface OrganizerEntity {
  mstr_Organizer_ID: number;
  organizer_Name: string | null;
  organizer_Code: string | null;
  mstr_Country_Id: number;
  country: string | null;
  address: string | null;
  city: string | null;
  email: string | null;
  phone: string | null;
  remarks: string | null;
  create_date: string;
  create_User: string;
  edit_Date: string;
  edit_User: string;
  mark_For_Delete: boolean;
  active: boolean;
  pageTitle: string;
}

export const organizerApi = createApi({
  reducerPath: 'organizerApi',
  baseQuery: authBaseQuery,
  tagTypes: ['Organizer'],
  endpoints: (builder) => ({
    getAllOrganizers: builder.query<OrganizerEntity[], void>({
      query: () => ({
        url: 'Organizer/GetAllOrganizers',
        method: 'GET',
      }),
      providesTags: ['Organizer']
    }),
    createOrganizer: builder.mutation<OrganizerEntity, Partial<OrganizerEntity>>({
      query: (body) => {
        console.log('Making create request with body:', body);
        return {
          url: 'Organizer/CreateOrganizer',
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['Organizer']
    }),    updateOrganizer: builder.mutation<OrganizerEntity, Partial<OrganizerEntity>>({
      query: (body) => ({
        url: `Organizer/UpdateOrganizer/${body.mstr_Organizer_ID}`,
        method: 'PUT',
        body: {
          mstr_Organizer_ID: body.mstr_Organizer_ID,
          organizer_Name: body.organizer_Name,
          organizer_Code: body.organizer_Code,
          mstr_Country_Id: body.mstr_Country_Id,
          country: body.country,
          address: body.address,
          city: body.city,
          email: body.email,
          phone: body.phone,
          remarks: body.remarks,
          is_Active: body.active,
          edit_User: body.edit_User,
          edit_Date: body.edit_Date || new Date().toISOString(),
          create_User: body.create_User,
          create_Date: body.create_date,
          mark_For_Delete: body.mark_For_Delete,
          pageTitle: "Master Organizer"
        }
      }),
      invalidatesTags: ['Organizer']
    }),
  }),
});

export const {
  useGetAllOrganizersQuery,
  useCreateOrganizerMutation,
  useUpdateOrganizerMutation,
} = organizerApi;